/**
 * pvdtees-bulk-variant-cart.js
 * Handles:
 *   1. Bulk variant selector – add multiple sizes/colors at once from product page
 *   2. Cart inline variant editor – change size/color directly in cart
 *
 * Add this file to the theme as assets/pvdtees-bulk-order.js
 * and include it in layout/theme.liquid before </body>:
 *   {{ 'pvdtees-bulk-order.js' | asset_url | script_tag }}
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
     PART 1 — BULK ORDER PANEL (product page)
  ───────────────────────────────────────────────────────────────────────── */

  function initBulkOrderPanels() {
    document.querySelectorAll('.bulk-order-wrapper').forEach(function (wrapper) {
      var toggle  = wrapper.querySelector('.bulk-order-toggle');
      var panel   = wrapper.querySelector('.bulk-order-panel');
      var inputs  = wrapper.querySelectorAll('.bulk-qty-input');
      var addBtn  = wrapper.querySelector('.bulk-add-to-cart-btn');
      var totalItems = wrapper.querySelector('.bulk-total-items');
      var totalPrice = wrapper.querySelector('.bulk-total-price');
      var messages   = wrapper.querySelector('.bulk-order-messages');

      if (!toggle || !panel) return;

      /* ── Toggle open/close ── */
      toggle.addEventListener('click', function () {
        var isOpen = panel.style.display !== 'none';
        panel.style.display = isOpen ? 'none' : 'block';
        toggle.setAttribute('aria-expanded', !isOpen);
        toggle.querySelector('.bulk-toggle-open').style.display = isOpen ? '' : 'none';
        toggle.querySelector('.bulk-toggle-close').style.display = isOpen ? 'none' : '';
      });

      /* ── Update totals on quantity change ── */
      function refreshTotals() {
        var itemCount = 0;
        var priceSum  = 0;
        inputs.forEach(function (input) {
          var qty = parseInt(input.value, 10) || 0;
          if (qty < 0) { input.value = 0; qty = 0; }
          itemCount += qty;
          priceSum  += qty * (parseInt(input.dataset.price, 10) || 0);
          input.classList.toggle('qty-active', qty > 0);
        });
        totalItems.textContent = itemCount;
        totalPrice.textContent = formatMoney(priceSum);
        addBtn.disabled = itemCount === 0;
      }

      inputs.forEach(function (input) {
        input.addEventListener('input',  refreshTotals);
        input.addEventListener('change', refreshTotals);
      });
      refreshTotals();

      /* ── Add all to cart ── */
      addBtn.addEventListener('click', function () {
        var items = [];
        inputs.forEach(function (input) {
          var qty = parseInt(input.value, 10) || 0;
          if (qty > 0) {
            items.push({ id: parseInt(input.dataset.variantId, 10), quantity: qty });
          }
        });
        if (!items.length) return;

        setLoading(addBtn, true, 'Adding…');
        messages.innerHTML = '';

        fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ items: items })
        })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (res.ok) {
            inputs.forEach(function (i) { i.value = 0; });
            refreshTotals();
            messages.innerHTML = '<p class="bulk-msg-success">✓ Items added to cart!</p>';
            refreshCartCount();
            document.dispatchEvent(new CustomEvent('cart:updated'));
          } else {
            messages.innerHTML = '<p class="bulk-msg-error">⚠ ' + (res.data.description || 'Could not add items. Please try again.') + '</p>';
          }
        })
        .catch(function () {
          messages.innerHTML = '<p class="bulk-msg-error">⚠ Network error. Please try again.</p>';
        })
        .finally(function () {
          setLoading(addBtn, false, 'Add All to Cart');
        });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     PART 2 — CART INLINE VARIANT EDITOR
  ───────────────────────────────────────────────────────────────────────── */

  function initCartVariantEditor() {
    var cartItems = document.querySelectorAll('.cart-item, [data-cart-item], .cart__item, tr.cart-item-row');
    if (!cartItems.length) return;
    cartItems.forEach(function (item) {
      var productHandle = item.dataset.productId || (item.querySelector('[data-product-id]') || {}).dataset && item.querySelector('[data-product-id]').dataset.productId;
      var variantId     = item.dataset.variantId  || (item.querySelector('[data-variant-id]')  || {}).dataset && item.querySelector('[data-variant-id]').dataset.variantId;
      var lineKey       = item.dataset.key         || (item.querySelector('[data-key]')          || {}).dataset && item.querySelector('[data-key]').dataset.key;
      if (!productHandle || !variantId) {
        var link = item.querySelector('a[href*="/products/"]');
        if (link) {
          var href = link.getAttribute('href');
          var hMatch = href.match(/\/products\/([^?#]+)/);
          var vMatch = href.match(/[?&]variant=(\d+)/);
          if (hMatch) productHandle = hMatch[1];
          if (vMatch) variantId = vMatch[1];
        }
      }
      if (!lineKey && item.id) {
        var lMatch = item.id.match(/CartItem-(\d+)/i) || item.id.match(/cart-item[_-](\d+)/i);
        if (lMatch) lineKey = 'line:' + lMatch[1];
      }
      if (!lineKey) lineKey = variantId;
      if (!productHandle) return;
      if (item.querySelector('.cart-variant-edit-trigger')) return;
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'cart-variant-edit-trigger';
      trigger.textContent = 'Change size/color';
      trigger.style.cssText = 'background:none;border:none;padding:0;font-size:12px;text-decoration:underline;cursor:pointer;color:#666;display:block;margin-top:4px;';
      var variantTitleEl = item.querySelector('.cart-item__variant, .product-option, .variant-title');
      if (variantTitleEl) {
        variantTitleEl.parentNode.insertBefore(trigger, variantTitleEl.nextSibling);
      } else {
        var nameEl = item.querySelector('.cart-item__name, .cart__product-title, .product-title');
        if (nameEl) nameEl.appendChild(trigger);
      }
      trigger.addEventListener('click', function () {
        openVariantEditor(item, trigger, productHandle, variantId, lineKey);
      });
    });
  }

  function openVariantEditor(item, trigger, productId, currentVariantId, lineKey) {
    var existing = item.querySelector('.cart-variant-editor');
    if (existing) { existing.remove(); trigger.textContent = 'Change size/color'; return; }
    trigger.textContent = 'Cancel';
    var editor = document.createElement('div');
    editor.className = 'cart-variant-editor';
    editor.style.cssText = 'margin-top:8px;padding:10px;background:#f9f9f9;border:1px solid #e1e1e1;border-radius:6px;font-size:13px;';
    editor.innerHTML = '<span style="color:#999">Loading options…</span>';
    trigger.parentNode.insertBefore(editor, trigger.nextSibling);
    fetch('/products/' + productId + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) { renderVariantEditor(editor, product, currentVariantId, lineKey, item, trigger); })
      .catch(function () { editor.innerHTML = '<span style="color:#c0392b">Could not load options.</span>'; });
  }

  function renderVariantEditor(editor, product, currentVariantId, lineKey, item, trigger) {
    var colors = [], sizes = [];
    product.options.forEach(function (opt, i) {
      var _n = typeof opt === 'string' ? opt : opt.name;
      if (_n.toLowerCase() === 'color') colors = product.variants.map(function (v) { return v['option'+(i+1)]; }).filter(onlyUnique);
      if (_n.toLowerCase() === 'size')  sizes  = product.variants.map(function (v) { return v['option'+(i+1)]; }).filter(onlyUnique);
    });
    var colorOpts = colors.map(function(c){ return '<option value="'+esc(c)+'">'+esc(c)+'</option>'; }).join('');
    var sizeOpts  = sizes.map(function(s){ return '<option value="'+esc(s)+'">'+esc(s)+'</option>'; }).join('');
    editor.innerHTML =
      (colorOpts ? '<label style="display:block;margin-bottom:6px;">Color: <select class="cve-color" style="margin-left:4px;border:1px solid #ccc;border-radius:3px;padding:3px 6px;">'+colorOpts+'</select></label>' : '') +
      (sizeOpts  ? '<label style="display:block;margin-bottom:6px;">Size: <select class="cve-size" style="margin-left:4px;border:1px solid #ccc;border-radius:3px;padding:3px 6px;">'+sizeOpts+'</select></label>'  : '') +
      '<button type="button" class="cve-apply" style="background:#000;color:#fff;border:none;border-radius:4px;padding:6px 14px;cursor:pointer;font-size:12px;font-weight:600;">Update Cart</button> <span class="cve-status" style="font-size:12px;margin-left:6px;"></span>';
    var current = product.variants.find(function(v){ return v.id == currentVariantId; });
    if (current) {
      var colorSel = editor.querySelector('.cve-color'), sizeSel = editor.querySelector('.cve-size');
      product.options.forEach(function (opt, i) {
      var _n = typeof opt === 'string' ? opt : opt.name;
        if (_n.toLowerCase()==='color' && colorSel) colorSel.value = current['option'+(i+1)];
        if (_n.toLowerCase()==='size'  && sizeSel)  sizeSel.value  = current['option'+(i+1)];
      });
    }
    editor.querySelector('.cve-apply').addEventListener('click', function () {
      var colorSel = editor.querySelector('.cve-color'), sizeSel = editor.querySelector('.cve-size');
      var selColor = colorSel ? colorSel.value : null, selSize = sizeSel ? sizeSel.value : null;
      var newVariant = product.variants.find(function(v){
        return product.options.every(function(opt,i){var _n=typeof opt==="string"?opt:opt.name;
          if (_n.toLowerCase()==='color' && selColor) return v['option'+(i+1)]===selColor;
          if (_n.toLowerCase()==='size'  && selSize)  return v['option'+(i+1)]===selSize;
          return true;
        });
      });
      if (!newVariant) { setStatus(editor,'⚠ Variant not found','error'); return; }
      if (newVariant.id == currentVariantId) { setStatus(editor,'Already selected','info'); return; }
      if (!newVariant.available) { setStatus(editor,'⚠ Sold out','error'); return; }
      setStatus(editor,'Updating…','info');
      var applyBtn = editor.querySelector('.cve-apply'); applyBtn.disabled = true;
      var currentQty = parseInt((item.querySelector('[data-quantity-input],input[name="updates[]"],input[name="quantity"]')||{}).value||'1',10)||1;
      var changeBody = (typeof lineKey==='string' && lineKey.indexOf('line:')===0)
        ? { line: parseInt(lineKey.replace('line:',''),10), quantity: 0 }
        : { id: lineKey || currentVariantId, quantity: 0 };
      fetch('/cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(changeBody)})
      .then(function(){ return fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({id:newVariant.id,quantity:currentQty})}); })
      .then(function(r){ return r.json().then(function(d){ return {ok:r.ok,data:d}; }); })
      .then(function(res){
        if (res.ok) { setStatus(editor,'✓ Cart updated! Refreshing…','success'); setTimeout(function(){ window.location.reload(); },800); }
        else { setStatus(editor,'⚠ '+(res.data.description||'Error updating cart'),'error'); applyBtn.disabled=false; }
      })
      .catch(function(){ setStatus(editor,'⚠ Network error.','error'); applyBtn.disabled=false; });
    });
  }

  /* ── HELPERS ── */
  function refreshCartCount() {
    fetch('/cart.js',{headers:{'Accept':'application/json'}}).then(function(r){return r.json();}).then(function(cart){
      document.querySelectorAll('.cart-count,.cart-quantity,[data-cart-count],.header__cart-count,.cart-count-bubble span').forEach(function(el){el.textContent=cart.item_count;});
    }).catch(function(){});
  }
  function setLoading(btn,loading,label){btn.disabled=loading;btn.textContent=label;}
  function setStatus(editor,msg,type){var el=editor.querySelector('.cve-status');if(!el)return;el.textContent=msg;el.style.color=type==='error'?'#c0392b':type==='success'?'#1a7a1a':'#666';}
  function onlyUnique(v,i,a){return a.indexOf(v)===i;}
  function esc(str){return(str||'').replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');}
  function formatMoney(cents){
    if(window.Shopify&&window.Shopify.formatMoney&&window.Shopify.money_format)return window.Shopify.formatMoney(cents,window.Shopify.money_format);
    return '$'+(cents/100).toFixed(2);
  }

  /* ── INIT ── */
  function init(){initBulkOrderPanels();initCartVariantEditor();}
  if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',init);}else{init();}
  document.addEventListener('cart:rendered',initCartVariantEditor);
  document.addEventListener('sections:load',function(){setTimeout(init,200);});

})();
